import { offlineDb } from "@/db/offlineDb";
import { materializeAggregate } from "./domain/reducer";
import {
  getActiveActorSession,
  getEventSigningPayloadString,
  signEventPayload,
} from "./transport/identity";

/**
 * Persist an event locally and update the materialized view.
 * 
 * In M0, we simply save it to Dexie and re-materialize the affected aggregate.
 * When the mesh is active (M1), this will also broadcast the event.
 */
export async function saveAndApplyEvent(event) {
  await offlineDb.transaction("rw", [offlineDb.captain_events, offlineDb.captain_materialized], async () => {
    // 1. Save the raw event
    // For M0, sync_state defaults to "local". Later it might be "synced" or "server_acked".
    const eventRecord = { ...event, _sync_state: "local" };
    await offlineDb.captain_events.put(eventRecord);

    // 2. Load all events for this aggregate to re-materialize
    const aggregateEvents = await offlineDb.captain_events.where("aggregate_id").equals(event.aggregate_id).toArray();
    
    // 3. Compute canonical state purely from history
    const state = materializeAggregate(aggregateEvents);

    // 4. Save materialized view for fast UI reads
    if (Object.keys(state).length > 0) {
      await offlineDb.captain_materialized.put({
        aggregate_id: event.aggregate_id,
        table_id: state.table_id,
        status: state.status,
        revision: state.revision,
        state: state
      });
    }
  });
}

/**
 * Loads the materialized state for a specific order.
 */
export async function loadMaterializedOrder(aggregateId) {
  if (!aggregateId) return null;
  return await offlineDb.captain_materialized.get(aggregateId);
}

/**
 * Loads the pure event history for an aggregate.
 */
export async function loadAggregateHistory(aggregateId) {
  return await offlineDb.captain_events.where("aggregate_id").equals(aggregateId).toArray();
}

/**
 * Fast-loads all currently active (open) orders on the device from the materialized view.
 */
export async function loadActiveMaterializedOrders() {
  return await offlineDb.captain_materialized.where("status").equals("open").toArray();
}

/* ------------------------------------------------------------------------- *
 * Reconciliation bookkeeping
 *
 * `_sync_state` is "local" until the SERVER says otherwise. The client does not
 * get to decide that an event synced — that claim is only ever a reflection of
 * a row in the backend's mesh_reconciled_events ledger, echoed back in the
 * reconcile response's acknowledged_event_ids.
 * ------------------------------------------------------------------------- */

export const SYNC_LOCAL = "local";
export const SYNC_SERVER_ACKED = "server_acked";
// The server refused the aggregate with a business conflict (TABLE_ALREADY_OCCUPIED).
// Retrying cannot change the answer, so rejected events leave the flush queue —
// otherwise every recovery re-asks a question whose answer is already no.
export const SYNC_REJECTED = "rejected";

/** Aggregates that still owe the server something (local only — not rejected). */
export async function loadUnsyncedAggregateIds() {
  const pending = await offlineDb.captain_events
    .where("_sync_state")
    .equals(SYNC_LOCAL)
    .toArray();
  return [...new Set(pending.map(e => e.aggregate_id))];
}

/**
 * The envelope fields the server accepts, and only those.
 *
 * A stored event is not a wire event: `saveAndApplyEvent` stamps `_sync_state`
 * on the record, which is this device's bookkeeping and no business of the
 * server's. POST /api/mesh/reconcile validates the envelope with
 * `extra="forbid"`, so leaking a local field is a 422 for the whole aggregate —
 * which is the schema doing its job, but the fix belongs here.
 *
 * Undefined keys drop out of JSON.stringify, and every one of these is optional
 * server-side, so a sparse event from an older build still validates.
 */
const WIRE_FIELDS = [
  "schema_version",
  "event_id",
  "device_id",
  "device_sequence",
  "aggregate_type",
  "aggregate_id",
  "parent_revision",
  "result_revision",
  "type",
  "payload",
  "created_at",
  "causation_id",
  "parent_event_id",
  // Who punched it, and the proof. Reconciliation skips any event without all
  // three ("missing cryptographic identity"), and derives orders.captain_id
  // from actor_id — omitting them made every captain event unsyncable: the
  // server read captain_id 0, no such user, and the aggregate retried its
  // foreign-key 500 every 15s forever.
  "actor_id",
  "actor_signature",
  "actor_authorization",
];

export function toWireEvent(record) {
  const wire = {};
  for (const key of WIRE_FIELDS) {
    if (record[key] !== undefined) wire[key] = record[key];
  }
  return wire;
}

/** The local event log for one aggregate, in causal order, wire-shaped. */
export async function loadAggregateForSync(aggregateId) {
  const events = await offlineDb.captain_events
    .where("aggregate_id")
    .equals(aggregateId)
    .toArray();
  return events
    .sort(
      (a, b) =>
        (a.result_revision || 0) - (b.result_revision || 0) ||
        (a.device_sequence || 0) - (b.device_sequence || 0)
    )
    .map(toWireEvent);
}

export async function markEventsAcknowledged(eventIds) {
  if (!eventIds || !eventIds.length) return 0;
  return await offlineDb.captain_events
    .where("event_id")
    .anyOf(eventIds)
    .modify({ _sync_state: SYNC_SERVER_ACKED });
}

/** The whole aggregate is refused — take it out of the flush queue for good. */

export async function markAggregateDiscarded(aggregateId) {
  if (!aggregateId) return;
  const existing = await offlineDb.mesh_order_map.get(aggregateId);
  await offlineDb.mesh_order_map.put({
    ...(existing || {}),
    aggregate_id: aggregateId,
    order_status: "discarded",
    updated_at: new Date().toISOString(),
  });
}

/**
 * Put a refused capture back in the flush queue, as if it had never been sent.
 *
 * For a CONFLICTED aggregate whose table nobody else holds: a capture
 * quarantined by mistake (the pre-2026-10-19 long-press bug did exactly this
 * to C05's and the owner's offline orders), or a table freed since the
 * refusal. The server stays the judge — if the table is taken after all, the
 * next flush answers with a real conflict and this aggregate is refused again.
 */
export async function reviveRejectedAggregate(aggregateId) {
  if (!aggregateId) return;
  await offlineDb.transaction("rw", [offlineDb.captain_events, offlineDb.mesh_order_map], async () => {
    await offlineDb.captain_events
      .where("aggregate_id")
      .equals(aggregateId)
      .modify(e => {
        if (e._sync_state === SYNC_REJECTED) e._sync_state = SYNC_LOCAL;
      });
    await offlineDb.mesh_order_map.delete(aggregateId);
  });
  await rematerializeAggregate(aggregateId);
}

export async function markAggregateRejected(aggregateId, conflictDetail) {
  if (!aggregateId) return;
  const currentState = await offlineDb.captain_materialized.get(aggregateId);
  const items = Object.values((currentState && currentState.state && currentState.state.items) || {}).filter(i => i.status !== "cancelled" && i.status !== "removed");

  await offlineDb.captain_events
    .where("aggregate_id")
    .equals(aggregateId)
    .modify({ _sync_state: SYNC_REJECTED });
  
  await offlineDb.mesh_order_map.put({
    aggregate_id: aggregateId,
    order_id: null,
    order_status: "CONFLICTED",
    conflict_detail: conflictDetail || { code: "CONFLICTED" },
    recovery_items: items
  });
  await rematerializeAggregate(aggregateId);
}

/**
 * Recompute the materialized view from the event log.
 *
 * The reducer reads each event's `_sync_state` to decide per-line KOT truth, and
 * `_sync_state` changes OUTSIDE the log (acknowledgement is a fact about an
 * event, not a new event). Whoever flips it must re-run the fold, or the screen
 * keeps showing the pre-acknowledgement world.
 */
export async function rematerializeAggregate(aggregateId) {
  // One rw transaction, same tables as saveAndApplyEvent, ON PURPOSE: this runs
  // right after a reconcile acknowledgement, which is exactly when the captain
  // is punching the next item. Un-transacted, this fold could read BEFORE that
  // punch's event landed and write AFTER its materialize did - silently wiping
  // the newest line from the projection. Dexie serialises rw transactions over
  // the same tables, which closes the window.
  await offlineDb.transaction("rw", [offlineDb.captain_events, offlineDb.captain_materialized], async () => {
    const events = await offlineDb.captain_events
      .where("aggregate_id")
      .equals(aggregateId)
      .toArray();
    if (!events.length) return;
    const state = materializeAggregate(events);
    if (Object.keys(state).length > 0) {
      await offlineDb.captain_materialized.put({
        aggregate_id: aggregateId,
        table_id: state.table_id,
        status: state.status,
        revision: state.revision,
        state,
      });
    }
  });
}

/**
 * Adopt a server-side order into the local mesh state.
 *
 * A table opened on the till (or before this device's IndexedDB existed) has a
 * real order the mesh knows nothing about — tapping it used to dead-end in an
 * options sheet because there was no aggregate to edit. Adoption imports the
 * server's truth as ALREADY-ACKNOWLEDGED events: an ORDER_STARTED carrying the
 * order's lines (kot-covered lines under an acked KOT_REQUESTED so the fold
 * marks them sent). Acked events never enter the flush queue, so adoption sends
 * NOTHING to the server — it only teaches this device what the server already
 * knows. New punches then flow through the ordinary mapped-aggregate diff.
 *
 * Idempotent per order: an existing mapping for the order returns its aggregate.
 */
export async function adoptServerOrder({ order, items, customer, tableDbId, deviceId, makeId }) {
  const existing = (await offlineDb.mesh_order_map.toArray()).find(
    m => m.order_id === order.id
  );
  if (existing) return existing.aggregate_id;

  const aggregateId = makeId();
  const live = items.filter(i => i.status !== "cancelled");
  // ONE line per distinct item state, not one per server row. The server holds
  // the same dish as several rows (the reconcile diff appends, the till
  // re-punches), and importing rows 1:1 put "Paneer ×1, Paneer ×1" on the
  // summary where every other path shows "Paneer ×2" — the menu consolidates
  // punches at the order layer, so adoption must import consolidated too.
  // KOT'd rows merge per KOT batch so the sent sections still read per ticket.
  const buckets = new Map();
  for (const i of live) {
    const key = [i.menu_item_id, i.variation_id ?? "", i.notes || "", i.kot_number ?? ""].join("|");
    const bucket = buckets.get(key);
    if (bucket) bucket.quantity += Number(i.quantity);
    else
      buckets.set(key, {
        menu_item_id: i.menu_item_id,
        item_name: i.item_name,
        unit_price: Number(i.unit_price),
        quantity: Number(i.quantity),
        notes: i.notes || null,
        kot_number: i.kot_number ?? null,
      });
  }
  const merged = [...buckets.values()];
  const lines = merged.map(b => ({
    line_id: makeId(),
    menu_item_id: b.menu_item_id,
    item_name: b.item_name,
    unit_price: b.unit_price,
    quantity: b.quantity,
    notes: b.notes,
    modifier_ids: [],
    variation_id: null,
  }));
  const kotted = merged
    .map((b, idx) => ({ kot: b.kot_number, line_id: lines[idx].line_id }))
    .filter(x => x.kot);

  const started = {
    schema_version: 1,
    event_id: makeId(),
    device_id: deviceId || null,
    aggregate_type: "ORDER",
    aggregate_id: aggregateId,
    device_sequence: 1,
    // parent_event_id must be EXPLICITLY null: sortCausalHistory roots only on
    // `=== null`, so leaving it undefined made every adopted event unreachable —
    // the fold came back empty, no materialized row was written, and the next
    // punch started a SECOND aggregate that the server rightly refused with
    // TABLE_ALREADY_OCCUPIED. That was the client half of the "opened by
    // another CaptainPAD" lock on every till-opened table.
    parent_event_id: null,
    parent_revision: 0,
    causation_id: null,
    result_revision: 1,
    type: "ORDER_STARTED",
    payload: {
      table_id: tableDbId,
      guest_count: order.guest_count,
      adopted_from_order: order.id,
      staff_name: order.captain_name || null,
      // The guest rides in like any ORDER_STARTED customer, so Edit Customer
      // and the summary block read them exactly as they read a mesh order's.
      customer: customer
        ? {
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            dob: customer.dob || "",
            anniversary: customer.anniversary || "",
          }
        : null,
      first_items: lines,
    },
    created_at: new Date().toISOString(),
  };
  const events = [started];
  if (kotted.length) {
    events.push({
      schema_version: 1,
      event_id: makeId(),
      device_id: deviceId || null,
      aggregate_type: "ORDER",
      aggregate_id: aggregateId,
      device_sequence: 2,
      parent_event_id: started.event_id,
      parent_revision: 1,
      causation_id: null,
      result_revision: 2,
      type: "KOT_REQUESTED",
      // `adopted: true` tells reconciliation this intent describes a KOT the
      // server itself already issued — record it, never print a second ticket.
      payload: { intent_id: makeId(), line_ids: kotted.map(k => k.line_id), adopted: true },
      created_at: new Date().toISOString(),
    });
  }

  // These events are hand-built here instead of going through
  // meshService.submitEvent, so nothing had stamped an actor onto them. The
  // server skipped every one as "missing cryptographic identity", and the
  // adopting ORDER_STARTED left mesh_order_map.user_id with no captain to name
  // — a foreign-key 500 the device then retried forever. Sign them the same way
  // a punched event is signed. device_id and actor_id are part of the signing
  // payload, so they must be set before the signature is taken.
  const session = getActiveActorSession();
  if (!session || !session.authorization) throw new Error("No active actor session");
  for (const ev of events) {
    ev.device_id = ev.device_id || session.device_id || null;
    ev.actor_id = String(session.authorization.user_id);
    ev.actor_authorization = session.authorization;
    ev.actor_signature = await signEventPayload(getEventSigningPayloadString(ev));
  }

  await offlineDb.transaction(
    "rw",
    [offlineDb.captain_events, offlineDb.captain_materialized, offlineDb.mesh_order_map],
    async () => {
      for (const ev of events) {
        await offlineDb.captain_events.put({ ...ev, _sync_state: SYNC_SERVER_ACKED });
      }
      await offlineDb.mesh_order_map.put({
        aggregate_id: aggregateId,
        order_id: order.id,
        order_number: order.order_number,
        order_status: order.status,
      });
    }
  );
  await rematerializeAggregate(aggregateId);
  return aggregateId;
}

/**
 * The local→server identity, plus what the server told us about the order the
 * last time we spoke: its number (for the header) and its status (so a closed
 * or cancelled order stops projecting as an occupied table).
 */
/**
 * Record what the SERVER says about an aggregate's order. Merged into the
 * existing row, never replacing it: a reconcile answer carries no table, and
 * wiping the server table a board refresh wrote put a moved order back on its
 * old table on this pad.
 *
 * `table_id` is the server's table for the order. It outranks the table the
 * local fold remembers (ORDER_STARTED's), because a Move Table on any terminal
 * changes the server's answer and no local event ever will — writing the new
 * table into the materialized row instead (as 2026-10-23 morning did) was
 * undone by the next re-fold, and the old table stayed "occupied" on the pad.
 */
export async function saveOrderMapping(aggregateId, { order_id, order_number, order_status, table_id, held_back } = {}) {
  if (!order_id) return;
  const existing = (await offlineDb.mesh_order_map.get(aggregateId)) || {};
  await offlineDb.mesh_order_map.put({
    ...existing,
    aggregate_id: aggregateId,
    order_id,
    ...(order_number != null ? { order_number } : {}),
    ...(order_status ? { order_status } : {}),
    ...(table_id != null ? { table_id } : {}),
    // The server's own words for what it held back on the last flush, so
    // "Not sent yet" can say why (law 3). Undefined leaves the last answer.
    ...(held_back !== undefined ? { held_back } : {}),
  });
}

/** The table an aggregate's order sits on: the server's, else the capture's. */
export function effectiveTableId(materialized, mapping) {
  return mapping?.table_id ?? materialized?.state?.table_id ?? materialized?.table_id ?? null;
}

/**
 * 24-hour retention for dead aggregates (owner-set policy, 2026-08-19).
 *
 * IndexedDB otherwise keeps every order this device ever captured, forever. A
 * live or still-unsynced aggregate is never touched — only aggregates whose
 * story is over (order closed/cancelled on the server, or the whole aggregate
 * quarantined as rejected) go, and only once their newest event is a day old.
 */
export const DEAD_AGGREGATE_TTL_MS = 24 * 60 * 60 * 1000;
const DEAD_STATUSES = new Set(["closed", "cancelled", "rejected", "CONFLICTED"]);

export async function pruneDeadAggregates(now = Date.now()) {
  const cutoff = now - DEAD_AGGREGATE_TTL_MS;
  const dead = (await offlineDb.mesh_order_map.toArray()).filter(m =>
    DEAD_STATUSES.has(m.order_status)
  );
  let pruned = 0;
  for (const m of dead) {
    const events = await offlineDb.captain_events
      .where("aggregate_id")
      .equals(m.aggregate_id)
      .toArray();
    const newest = Math.max(0, ...events.map(e => Date.parse(e.created_at) || 0));
    if (newest > cutoff) continue;
    await offlineDb.transaction(
      "rw",
      [offlineDb.captain_events, offlineDb.captain_materialized, offlineDb.mesh_order_map],
      async () => {
        await offlineDb.captain_events.where("aggregate_id").equals(m.aggregate_id).delete();
        await offlineDb.captain_materialized.delete(m.aggregate_id);
        await offlineDb.mesh_order_map.delete(m.aggregate_id);
      }
    );
    pruned += 1;
  }
  if (pruned) console.log(`[mesh] pruned ${pruned} dead aggregate(s) past 24h retention`);
  return pruned;
}

/**
 * Abandoned drafts: an aggregate with no lines and no KOT ever requested is an
 * order that came to nothing — the captain opened a table, punched items, took
 * them all back and walked away. §39.2 gives a captain the whole-order cancel;
 * this automates it at the moment the story is visibly over (the tables board
 * is on screen), so an empty order does not hold the table for the rest of
 * service showing "punching".
 *
 * Server truth outranks the local fold: when the draft reconciled to a real
 * order, that order is re-read first and cancelled ONLY while it is still
 * `open` with zero live items — another terminal may have punched food onto
 * it. The cancel itself goes through the same server command a manual void
 * uses, passed in as `cancelOnServer` so this Dexie layer stays free of HTTP.
 * A draft that never reconciled is discarded on-device — there is nothing
 * anywhere else to end.
 *
 * Never touched: aggregates with lines, aggregates that asked for a KOT
 * (kitchen involvement makes every ending an explicit human decision), and
 * mapped drafts while offline — those retry on the next board visit.
 */
export async function sweepAbandonedDrafts({
  isOnline,
  fetchOrder,
  fetchItems,
  cancelOnServer,
}) {
  const LIVE = new Set(["open", "kot_sent", "billing"]);
  const all = await offlineDb.captain_materialized.toArray();
  let swept = 0;
  for (const m of all) {
    // Live lines only. A removed line stays in the fold as a "removed"
    // tombstone, so counting every entry kept a table whose items were all
    // taken back (never fired) on PUNCHING for good - 1 Gardenn, order #3,
    // 2026-10-19 23:10. Whether it was ever fired is the KOT check below.
    const lines = Object.values(m.state?.items || {}).filter(
      i => i.status !== "removed" && i.status !== "cancelled" && !(Number(i.quantity) <= 0)
    );
    if (lines.length) continue;
    const kotCount = await offlineDb.captain_events
      .where("[aggregate_id+type]")
      .equals([m.aggregate_id, "KOT_REQUESTED"])
      .count();
    if (kotCount) continue;

    const mapping = await offlineDb.mesh_order_map.get(m.aggregate_id);
    if (!mapping) {
      await offlineDb.transaction(
        "rw",
        [offlineDb.captain_events, offlineDb.captain_materialized],
        async () => {
          await offlineDb.captain_events
            .where("aggregate_id")
            .equals(m.aggregate_id)
            .delete();
          await offlineDb.captain_materialized.delete(m.aggregate_id);
        }
      );
      swept += 1;
      continue;
    }

    if (mapping.order_status && !LIVE.has(mapping.order_status)) continue; // already ended; the 24h pruner owns it
    if (!isOnline) continue;
    try {
      const order = await fetchOrder(mapping.order_id);
      if (order.status !== "open") {
        // The mapping was stale — record the server's ending (or its KOT) and
        // let orderForTable / the pruner act on the truth.
        await saveOrderMapping(m.aggregate_id, {
          order_id: mapping.order_id,
          order_status: order.status,
        });
        continue;
      }
      const items = await fetchItems(mapping.order_id);
      if ((items || []).some(i => i.status !== "cancelled")) continue;
      await cancelOnServer(mapping.order_id, order.version);
      await saveOrderMapping(m.aggregate_id, {
        order_id: mapping.order_id,
        order_status: "cancelled",
      });
      swept += 1;
    } catch {
      // Next board visit retries; an abandon must never take the board down.
    }
  }
  if (swept) console.log(`[mesh] swept ${swept} abandoned draft(s)`);
  return swept;
}

export async function getOrderMapping(aggregateId) {
  if (!aggregateId) return null;
  const row = await offlineDb.mesh_order_map.get(aggregateId);
  return row ? row.order_id : null;
}

/**
 * The live aggregate holding a table, straight from Dexie — for callbacks whose
 * closures hold a stale render (conflict resolution re-resolves its target AFTER
 * the refused aggregate was quarantined, so it cannot trust hook state).
 */
export async function findLiveAggregateForTable(tableDbId) {
  const LIVE = new Set(["open", "kot_sent", "billing"]);
  const all = await offlineDb.captain_materialized.toArray();
  const maps = new Map(
    (await offlineDb.mesh_order_map.toArray()).map(m => [m.aggregate_id, m])
  );
  // Newest capture first: an older live-looking aggregate on the same table is
  // an earlier party whose ending this device has not heard yet.
  const live = all.filter(m => {
    const mapped = maps.get(m.aggregate_id);
    const status =
      mapped?.order_status && !LIVE.has(mapped.order_status)
        ? mapped.order_status
        : m.state.status;
    return effectiveTableId(m, mapped) === tableDbId && LIVE.has(status);
  });
  live.sort((a, b) => (Date.parse(b.state?.created_at) || 0) - (Date.parse(a.state?.created_at) || 0));
  return live[0] || null;
}

/**
 * The server order running on a table, from the live board — by table id only.
 *
 * Every screen that turns "this table" into "this order" (KOT history, Print
 * Bill, Cancel) used to take the FIRST local capture with that table id — often
 * a finished order from earlier in the day — and fall back to matching the
 * board on table NUMBER, which repeats across areas. Both put old or foreign
 * KOTs and bills on a table (Mahendra, Garden 2, 2026-10-23). Online, the board
 * is the answer.
 */
export function serverOrderOnTable(board, tableDbId) {
  const rows = (board || []).filter(o => o.table_id === tableDbId);
  return rows.find(o => o.status !== "billing") || rows[0] || null;
}

export async function getOrderMappingRow(aggregateId) {
  if (!aggregateId) return null;
  return (await offlineDb.mesh_order_map.get(aggregateId)) || null;
}

/**
 * Has the server issued a KOT for this aggregate?
 *
 * A KOT_REQUESTED event that is still "local" is an intent the kitchen has not
 * been told about (bible §39.1). The summary screen reads this to decide between
 * "Pending Server" and "KOT Sent", so it must never be inferred from local state.
 */
export async function kotIsServerAcknowledged(aggregateId) {
  if (!aggregateId) return false;
  const kots = await offlineDb.captain_events
    .where("[aggregate_id+type]")
    .equals([aggregateId, "KOT_REQUESTED"])
    .toArray();
  return kots.length > 0 && kots.every(e => e._sync_state === SYNC_SERVER_ACKED);
}

/**
 * Record on this device the orders another device ended (owner, 2026-10-24: a
 * cancelled table still showed as occupied on one or two phones). A phone heard
 * that its order ended only when it synced that order itself. Given the live
 * board's order ids, every mapped order still marked live here but missing from
 * the board is read from the server; closed/cancelled (or gone, 404) is saved as
 * its status. Anything else — still live, or the read failed — is left alone.
 * Returns how many were retired.
 */
export async function retireEndedAggregates({ boardOrderIds, fetchOrder }) {
  const LIVE = new Set(["open", "kot_sent", "billing"]);
  const onBoard = new Set((boardOrderIds || []).map(Number));
  const maps = await offlineDb.mesh_order_map.toArray();
  let retired = 0;
  for (const m of maps) {
    // No status yet reads as live, as the boards read it.
    if (m.order_id == null || (m.order_status && !LIVE.has(m.order_status)) || onBoard.has(Number(m.order_id))) continue;
    let status = null;
    try {
      status = (await fetchOrder(m.order_id))?.status ?? null;
    } catch (failure) {
      if (failure?.response?.status === 404) status = "cancelled";
      else continue;
    }
    if (status === "closed" || status === "cancelled") {
      await offlineDb.mesh_order_map.update(m.aggregate_id, { order_status: status });
      retired += 1;
    }
  }
  return retired;
}
